import React, { useEffect, useState } from 'react';
import CarehomeAddress from '@components/team-member/organization-details/components/CarehomeAddress';
import useGetCarehomeInfo from '@hooks/useGetCarehomeInfo';
import { usePartner } from '@contexts/PartnerContext';
import { CarehomeAddressType } from '@typings/CarehomeInfo';
import { formatPossessive } from '@utils/commons';
import { useNavigate, useLocation } from 'react-router-dom';
import AddressType from './AddressType';
// eslint-disable-next-line import/no-cycle
import { AddressDataType } from '../index';

interface CurrentAddressProps {
  fullName: string;
  setCurrentAddress: (address: CarehomeAddressType) => void;
  currentAddressType: string;
  setCurrentAddressType: (value: string) => void;
  isReside: boolean;
  setIsReside: React.Dispatch<React.SetStateAction<boolean>>;
  addressFromData: AddressDataType;
}

const CurrentAddress: React.FC<CurrentAddressProps> = ({
  fullName,
  setCurrentAddress,
  currentAddressType,
  setCurrentAddressType,
  isReside,
  setIsReside,
  addressFromData,
}) => {
  const [addressType, setAddressType] = useState('');
  const [previousAddress, setPreviousAddress] = useState<CarehomeAddressType>({
    addressLine1: '',
    addressLine2: '',
    city: '',
    county: '',
    postCode: '',
  });

  const [carehomeAddress, setCarehomeAddress] = useState<CarehomeAddressType>({
    addressLine1: '',
    addressLine2: '',
    city: '',
    county: '',
    postCode: '',
  });

  const { selectedPartnerId } = usePartner();
  const location = useLocation();
  const navigate = useNavigate();

  const { savedCarehomeAddressFromTeams } =
    useGetCarehomeInfo(selectedPartnerId);

  const handleCheckboxChange = () => {
    setIsReside(prevIsReside => {
      const newIsReside = !prevIsReside;

      if (newIsReside) {
        // Save current address before switching to saved one
        setPreviousAddress(carehomeAddress);
        setCarehomeAddress(savedCarehomeAddressFromTeams);
      } else {
        // Restore previous address when unchecked
        setCarehomeAddress(previousAddress);
      }

      return newIsReside;
    });
  };

  useEffect(() => {
    if (addressFromData) {
      setCarehomeAddress(addressFromData.currentAddress);
      setAddressType(addressFromData.currentAddressType);
      if (typeof addressFromData.isResideInCurrentAddress !== 'undefined') {
        setIsReside(addressFromData.isResideInCurrentAddress);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressFromData]);

  useEffect(() => {
    setCurrentAddressType(addressType);
  }, [addressType, setCurrentAddressType]);

  useEffect(() => {
    setCurrentAddress(carehomeAddress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carehomeAddress]);

  useEffect(() => {
    const scrollToId = (location.state as any)?.scrollTo;
    if (scrollToId) {
      setTimeout(() => {
        const el = document.getElementById(scrollToId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }

        // Clear the scroll state to prevent it from running again
        navigate(location.pathname, { replace: true, state: {} });
      }, 50);
    }
  }, [location, navigate]);

  return (
    <div id="currentAddress">
      <h1 className="govuk-heading-l mt-10 govuk-bp:mt-14">
        {fullName
          ? `${formatPossessive(fullName)} current address`
          : 'Current address'}
      </h1>
      <AddressType
        addressType={currentAddressType}
        setAddressType={setCurrentAddressType}
      />

      <div className="govuk-form-group">
        <fieldset
          className="govuk-fieldset"
          aria-describedby="asigned-nok-hint"
        >
          <div className="govuk-checkboxes" data-module="govuk-checkboxes">
            <div className="govuk-checkboxes__item">
              <input
                className="govuk-checkboxes__input"
                id="acp-completion"
                name="acp-copletion"
                type="checkbox"
                checked={isReside}
                onChange={handleCheckboxChange}
              />
              <label
                className="govuk-label govuk-checkboxes__label"
                htmlFor="acp-completion"
              >
                If {fullName} now permanently resides in your hospice, care home
                or other care setting please check the box provided
              </label>
            </div>
          </div>
        </fieldset>
      </div>
      <fieldset className="govuk-fieldset mt-10 govuk-bp:mt-14">
        <CarehomeAddress
          isDisabled={isReside}
          savedCarehomeAddressFromTeams={savedCarehomeAddressFromTeams}
          carehomeAddress={carehomeAddress}
          setCarehomeAddress={setCarehomeAddress}
          notShowInitialAddress
        />
      </fieldset>
    </div>
  );
};

export default CurrentAddress;
